/**
 * PostitsMasterPanel — visão consolidada para o mestre.
 *
 * Mostra contadores por coluna (ou total se modo free), top reagidos,
 * e botões pra deletar (moderar). Atualiza em tempo real via usePostits.
 */

import { useAdapter } from '../../../adapter'
import type { MissionComponentProps } from '../../../games/atlas/components'
import { usePostits } from '../usePostits'

interface Column {
  id: string
  label: string
  color?: string
}

export function PostitsMasterPanel(props: MissionComponentProps) {
  const adapter = useAdapter()
  const slideIndex = props.slideIndex ?? 0
  const config = props.slideConfig ?? {}
  const mode = ((config.mode as string) || 'free') as 'free' | 'columns'
  const columns: Column[] = Array.isArray(config.columns)
    ? (config.columns as Column[])
    : []
  const postits = usePostits(slideIndex)

  const total = postits.length
  const topReacted = [...postits]
    .sort((a, b) => b.reactionCount - a.reactionCount)
    .slice(0, 5)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <header>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Mural — visão consolidada</h3>
        <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '0.85em' }}>
          {total} post-it(s) colado(s)
        </p>
      </header>

      {mode === 'columns' && columns.length > 0 && (
        <section>
          <div style={{ fontSize: '0.8em', opacity: 0.7, marginBottom: 4 }}>Por coluna</div>
          {columns.map((c) => {
            const count = postits.filter((p) => p.columnId === c.id).length
            return (
              <div
                key={c.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 8px',
                  borderLeft: `3px solid ${c.color || 'var(--color-lab-accent, #5B2DB8)'}`,
                  fontSize: '0.85em',
                  marginBottom: 2,
                }}
              >
                <span>{c.label}</span>
                <span>{count}</span>
              </div>
            )
          })}
        </section>
      )}

      {topReacted.length > 0 && topReacted[0].reactionCount > 0 && (
        <section>
          <div style={{ fontSize: '0.8em', opacity: 0.7, marginBottom: 4 }}>
            Mais reagidos
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {topReacted.filter((p) => p.reactionCount > 0).map((p) => (
              <li
                key={p.id}
                style={{
                  padding: 8,
                  background: '#fff8b8',
                  borderRadius: 4,
                  fontSize: '0.8em',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <div style={{ whiteSpace: 'pre-wrap' }}>{p.text}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', opacity: 0.6 }}>
                  <span>♥ {p.reactionCount}</span>
                  <button
                    onClick={() => adapter.deletePostit({ postitId: p.id })}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#d4474a',
                      fontSize: '0.85em',
                    }}
                    title="Moderar (remover)"
                  >
                    moderar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {total === 0 && (
        <div style={{ opacity: 0.6, fontStyle: 'italic', fontSize: '0.9em' }}>
          Aguardando post-its dos alunos…
        </div>
      )}
    </div>
  )
}
